import { type MaybeRefOrGetter, onBeforeUnmount, onMounted, toValue } from 'vue'

/**
 * Re-creates a `background-attachment: fixed` parallax in JavaScript for
 * browsers that mishandle it — notably WebKit/Safari, where it is unsupported
 * on iOS and drops the background on scroll inside clipped `foreignObject`s on
 * desktop.
 *
 * This composable owns only the plumbing: a single rAF-throttled, capturing
 * `scroll` listener (capture phase also catches non-bubbling scrolls from nested
 * containers, e.g. a horizontal carousel) plus a `resize` listener, attached on
 * mount and torn down on unmount. The caller supplies `paint`, which reads each
 * target element's viewport position and updates its `background-position` to
 * keep the background pinned to the viewport.
 *
 * @param enabled - Whether the fallback should run (e.g. only on Safari). When
 *   false, no listeners are attached and the native CSS path is left untouched.
 * @param paint - Repaints the background position(s). Called once on mount and
 *   on every throttled scroll/resize frame.
 */
export function useScrollPinnedBackground(
  enabled: MaybeRefOrGetter<boolean>,
  paint: () => void,
) {
  let rafId: number | null = null
  let scheduled = false

  function schedule() {
    if (scheduled) return
    scheduled = true
    rafId = requestAnimationFrame(() => {
      scheduled = false
      paint()
    })
  }

  onMounted(() => {
    if (!toValue(enabled)) return

    paint()
    window.addEventListener('scroll', schedule, {
      capture: true,
      passive: true,
    })
    window.addEventListener('resize', schedule, { passive: true })
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', schedule, { capture: true })
    window.removeEventListener('resize', schedule)
    if (rafId !== null) cancelAnimationFrame(rafId)
  })
}
